/**
 * 회원 관리 — 목록(EA-06)·상세(EA-07)·정지/해제(EA-43)·등급 변경(EA-44)·스페셜 퇴출(EA-45).
 *
 * 계약: XM-23(목록, offset)·XM-24(상세)·XM-25(등급 이력)·XM-29(KYC)·XM-26·XM-27·XM-28.
 *
 * ⚠ **정지 기준(사장님 2026-10-02 「거래 정지 기준」)**: 거래 중인 회원의 정지는 그 거래가 끝나면 걸린다 — 서버가
 * `pendingSuspension`(거래 번호)으로 알려 준다. 목록·상세는 상태 옆에 「Suspends when the trade ends (거래 번호)」,
 * 정지 뒤 답이 그렇게 오면 알림을 띄운다. 걸어 둔 회원의 버튼은 「Lift suspension」(걸어 둔 것을 지움).
 *
 * ⚠ **계약에 있는 칸만 그린다.** 레거시 「수수료 상태」·이메일 검색·소속 코드 검색은
 * 계약에 없어 뺐다(명세 7절 C-02·C-03). 계산 안 된 값은 `Maybe` 가 「—」로 그린다.
 *
 * ⚠ **주소는 `/users/{memberId}` 다**(명세 0-6 고침 — 레거시는 아이디).
 *
 * ⚠ **패널티 잠금(2026-10-29 사장님 앱 수정 D)**: 사고처리 중 거래를 관리자가 거래취소로 끝내면 그 거래의 구매자가 잠긴다(로그인·조회만,
 * 관리자가 풀 때까지). 상세의 「Penalty lock」 줄에 잠근 때·거래 번호·그 거래취소를 요청한 관리자, 버튼 [Release penalty lock](XM-125,
 * `members.status.edit`) — 고객센터 전화로 해제를 원할 때.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import {
  changeMemberGrade, expelMember, getMember, getMemberKyc, listGradeHistory, listMembers,
  releasePenaltyLock, setMemberStatus,
} from '@/api/admin'
import { findMemberId } from '@/api/lookup'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import {
  AdminRef, Badge, Button, Card, Code, Field, Labeled, LINK, Maybe, MemberLink, Notice, Row, TD, TextArea,
  TH, Time,
} from '@/components/ui'
import { Confirm, sendWrite, submitWrite, useWrite } from '@/components/Confirm'
import { EnglishOnlyNote, hasKorean, REASON_MAIL_HINT } from '@/components/EnglishOnly'
import { ApiError, errorMessage } from '@/api/errors'
import { QueryState } from '@/components/QueryState'
import { OffsetPager } from '@/components/Pager'
import { Choice, FilterBar, idParam, optionsOf, Search, useFilters } from '@/components/Filters'
import { formatTime, usde } from '@/format'
import {
  APPROVED_BY_LABEL, GRADE_LABEL, GRADE_REASON_LABEL, KYC_PROVIDER_LABEL, KYC_STATUS_LABEL, MEMBER_STATUS_LABEL,
  PARTNER_STATUS_LABEL, WARNING_LEVEL_LABEL,
  type MemberDetail, type MemberGrade, type PenaltyLock, type PendingSuspension, type WalletSuspension,
} from '@/api/types'

const LIMIT = 25
const USER_FILTERS = ['loginId', 'grade', 'status', 'kycStatus', 'masterId'] as const
const HISTORY_FILTERS = ['reasonType'] as const

/** 「거래가 끝나면 정지됨(거래 번호)」 — 상태는 그때까지 Normal(ACTIVE). */
function PendingSuspensionBadge({ pending }: { pending: PendingSuspension | null | undefined }) {
  if (!pending) return null
  return <Badge tone="warn">Suspends when the trade ends ({pending.tradeId})</Badge>
}

/**
 * 지갑 정지(2026-10-02, 거래소 01 2-24 — 재단 홈페이지 요청으로 지갑 백엔드가 알린 등록 지갑의 정지). 회원 상태와 따로다 —
 * 상태 칸은 그대로 두고 옆에 붙인다.
 */
function WalletSuspensionBadge({ wallet }: { wallet: WalletSuspension | null | undefined }) {
  if (!wallet) return null
  return wallet.status === 'suspended'
    ? <Badge tone="stop">Wallet suspended</Badge>
    : <Badge tone="warn">Wallet suspends when the trade ends</Badge>
}

/** 패널티 잠금(2026-10-29) — 잠근 때·거래 번호·그 거래취소를 요청한 관리자. 잠기지 않았으면 「Not locked」. */
function PenaltyLockLine({ lock }: { lock: PenaltyLock | null | undefined }) {
  if (!lock?.locked) return <span className="text-slate-500">Not locked</span>
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone="stop">Penalty locked</Badge>
      <span>since <Time ms={lock.lockedAt} /></span>
      <span>· trade <span className="font-mono">{lock.orderId}</span></span>
      {lock.lockedByAdminId != null && (
        <span>· cancelled by <AdminRef adminId={lock.lockedByAdminId} loginId={lock.lockedByAdminLoginId} empty="—" /></span>
      )}
    </span>
  )
}

/* ── EA-06 회원 목록 ──────────────────────────────────────────────── */

export function UsersPage() {
  const f = useFilters(USER_FILTERS)
  const params = {
    loginId: f.values['loginId'] || undefined,
    grade: f.values['grade'] || undefined,
    status: f.values['status'] || undefined,
    kycStatus: f.values['kycStatus'] || undefined,
    masterId: idParam(f.values['masterId']),
    offset: f.offset,
    limit: LIMIT,
  }
  const q = useQuery({ queryKey: ['members', params], queryFn: () => listMembers(params) })

  return (
    <Card title="Members">
      <FilterBar active={f.active} onClear={f.clear}>
        <Search label="ID" value={f.values['loginId'] ?? ''}
          placeholder="Part of the ID"
          onChange={(v) => f.set('loginId', v)} />
        <Choice label="Grade" value={f.values['grade'] ?? ''}
          options={optionsOf(GRADE_LABEL)} onChange={(v) => f.set('grade', v)} />
        <Choice label="Status" value={f.values['status'] ?? ''}
          options={optionsOf(MEMBER_STATUS_LABEL)} onChange={(v) => f.set('status', v)} />
        <Choice label="KYC" value={f.values['kycStatus'] ?? ''}
          options={optionsOf(KYC_STATUS_LABEL)} onChange={(v) => f.set('kycStatus', v)} />
        <Search label="Master no." value={f.values['masterId'] ?? ''} inputMode="numeric"
          onChange={(v) => f.set('masterId', v)} />
      </FilterBar>

      <QueryState isPending={q.isPending} error={q.error} empty={q.data?.length === 0}
        emptyText="No members match the filters.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>ID</th><th className={TH}>Email</th><th className={TH}>Grade</th>
            <th className={TH}>Status</th><th className={TH}>KYC</th><th className={TH}>Master</th>
            <th className={TH}>Wallet</th>
          </tr></thead>
          <tbody>
            {(q.data ?? []).map((u) => (
              <tr key={u.memberId}>
                <td className={TD}><MemberLink memberId={u.memberId} loginId={u.loginId} /></td>
                <td className={TD}>{u.email}</td>
                <td className={TD}><Labeled labels={GRADE_LABEL} code={u.grade} /></td>
                <td className={TD}>
                  <Labeled labels={MEMBER_STATUS_LABEL} code={u.status} />
                  <PendingSuspensionBadge pending={u.pendingSuspension} />
                </td>
                <td className={TD}><Labeled labels={KYC_STATUS_LABEL} code={u.kycStatus} /></td>
                {/* ⚠ 목록에는 마스터 번호만 온다(7절 C-03) — 「마스터 #번호」 링크. */}
                <td className={TD}>
                  {u.masterId === null
                    ? <Maybe value={null} />
                    : <Link className={LINK} to={`/users/${u.masterId}`}>Master #{u.masterId}</Link>}
                </td>
                {/* ⚠ 목록은 등록 여부만 — 주소는 상세에서 본다. */}
                <td className={TD}>{u.walletAddress ? 'Registered' : 'Not registered'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>

      <OffsetPager offset={f.offset} limit={LIMIT} count={q.data?.length} onChange={f.setOffset} />
    </Card>
  )
}

/* ── EA-07 회원 상세 ──────────────────────────────────────────────── */

export function UserDetailPage() {
  const { memberId: raw = '' } = useParams()
  const memberId = Number(raw)
  const { has } = useSession()
  const f = useFilters(HISTORY_FILTERS)
  const canKyc = has('members.kyc.view')
  const canHistory = has('members.grade_history.view')

  // ⚠ 세 창구를 따로 부른다 — 하나가 실패해도 나머지는 그린다(명세 EA-07).
  const u = useQuery({ queryKey: ['member', memberId], queryFn: () => getMember(memberId) })
  const k = useQuery({
    queryKey: ['member-kyc', memberId], queryFn: () => getMemberKyc(memberId), enabled: canKyc,
  })
  const h = useQuery({
    queryKey: ['grade-history', memberId], queryFn: () => listGradeHistory(memberId),
    enabled: canHistory,
  })

  // 사유 거르기는 받은 목록 안에서 — XM-25 에 거르개가 없다(제안 03 4번).
  const reason = f.values['reasonType'] ?? ''
  const history = (h.data ?? []).filter((r) => reason === '' || r.reasonType === reason)
  const m = u.data

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/users" className="text-xs text-slate-500 underline">← Member list</Link>
      </div>

      <Card title={m ? `Member ${m.loginId}` : `Member #${raw}`}
        actions={m ? <MemberActions member={m} /> : undefined}>
        <QueryState isPending={u.isPending} error={u.error}>
          {m && (
            <dl className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
              <Row label="Email">{m.email}</Row>
              <Row label="Grade"><Labeled labels={GRADE_LABEL} code={m.grade} /></Row>
              <Row label="Status">
                <Labeled labels={MEMBER_STATUS_LABEL} code={m.status} />
                <PendingSuspensionBadge pending={m.pendingSuspension} />
                <WalletSuspensionBadge wallet={m.walletSuspension} />
              </Row>
              <Row label="Penalty lock"><PenaltyLockLine lock={m.penaltyLock} /></Row>
              <Row label="KYC status"><Labeled labels={KYC_STATUS_LABEL} code={m.kycStatus} /></Row>
              <Row label="KYC verified at"><Time ms={m.kycVerifiedAt} /></Row>
              <Row label="Master">
                <MemberLink memberId={m.masterId} loginId={m.masterLoginId} />
              </Row>
              {/* 레거시 '내 코드' — 파트너의 추천 코드. */}
              <Row label="Referral code"><Maybe value={m.partner?.referralCode ?? null} /></Row>
              <Row label="Wallet address">
                <span className="break-all font-mono text-xs"><Maybe value={m.walletAddress} /></span>
              </Row>
              <Row label="Collateral">
                <span className="font-mono tabular-nums">
                  <Maybe value={usde(m.collateral?.balance ?? null)} />
                </span>
                {m.collateral != null && (
                  <span className="ml-2 text-xs text-slate-500">
                    (read at {formatTime(m.collateral.balanceReadAt) ?? '—'}{!m.collateral.balanceLive && ' · stored value'})
                  </span>
                )}
              </Row>
              <Row label="Partner status">
                <Labeled labels={PARTNER_STATUS_LABEL}
                  code={m.partner?.partnerStatus ?? m.collateral?.partnerStatus ?? null} />
              </Row>
              <Row label="Warning level">
                <Labeled labels={WARNING_LEVEL_LABEL} code={m.partner?.warningLevel ?? null} />
              </Row>
              <Row label="Joined at"><Time ms={m.createdAt} /></Row>
              <Row label="Account closed at"><Time ms={m.withdrawnAt} /></Row>
            </dl>
          )}
        </QueryState>
      </Card>

      {/* ⚠ 키가 없는 카드는 그리지 않는다(명세 EA-07). */}
      {canKyc && (
        <Card title="KYC">
          <QueryState isPending={k.isPending} error={k.error}>
            {k.data && (
              <dl className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
                <Row label="Status"><Labeled labels={KYC_STATUS_LABEL} code={k.data.status} /></Row>
                <Row label="Provider"><Labeled labels={KYC_PROVIDER_LABEL} code={k.data.provider} /></Row>
                <Row label="SUMSUB applicant ID">
                  <span className="font-mono text-xs"><Maybe value={k.data.applicantId} /></span>
                </Row>
                <Row label="Review result">
                  <span className="flex flex-wrap gap-1">
                    {k.data.reviewAnswer || k.data.reviewRejectType || (k.data.rejectLabels?.length ?? 0) > 0
                      ? <>
                          {k.data.reviewAnswer && <Code>{k.data.reviewAnswer}</Code>}
                          {k.data.reviewRejectType && <Code>{k.data.reviewRejectType}</Code>}
                          {(k.data.rejectLabels ?? []).map((l) => <Code key={l}>{l}</Code>)}
                        </>
                      : <Maybe value={null} />}
                  </span>
                </Row>
                <Row label="Started at"><Time ms={k.data.startedAt} /></Row>
                <Row label="Verified at"><Time ms={k.data.verifiedAt} /></Row>
                <Row label="Reviewed at"><Time ms={k.data.reviewedAt} /></Row>
                {/* 2026-10-12 (물음 16 ⑤ — 가입 때 KYC): 이 회원을 만든 가입 신청과 그 신원 인증을 승인한 쪽 */}
                <Row label="Sign-up application">
                  {k.data.signupApplicationId
                    ? <Link className={LINK} to={`/kyc-verification/${k.data.signupApplicationId}`}>{k.data.signupApplicationId}</Link>
                    : <Maybe value={null} />}
                </Row>
                <Row label="Approved by">
                  {k.data.approvedBy ? APPROVED_BY_LABEL[k.data.approvedBy] : <Maybe value={null} />}
                </Row>
              </dl>
            )}
          </QueryState>
        </Card>
      )}

      {canHistory && (
        <Card title="Grade change history">
          <FilterBar active={f.active} onClear={f.clear}>
            <Choice label="Reason" value={reason}
              options={optionsOf(GRADE_REASON_LABEL)} onChange={(v) => f.set('reasonType', v)} />
          </FilterBar>
          <QueryState isPending={h.isPending} error={h.error} empty={history.length === 0}
            emptyText="The grade has never changed.">
            <table className="w-full text-sm">
              <thead><tr>
                <th className={TH}>Change</th><th className={TH}>Reason</th><th className={TH}>Master</th>
                <th className={TH}>Approved by</th><th className={TH}>Time</th>
              </tr></thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id}>
                    <td className={TD}>{r.previousGrade ?? '—'} → {r.newGrade}</td>
                    <td className={TD}>
                      <Labeled labels={GRADE_REASON_LABEL} code={r.reasonType} />
                      {r.reason && <span className="ml-2 text-slate-600 dark:text-slate-300">{r.reason}</span>}
                    </td>
                    <td className={TD}><MemberLink memberId={r.masterId} /></td>
                    {/* ⚠ 빈칸은 「승인자 없음」이 아니라 회원 본인·마스터 조직 행위다. */}
                    <td className={TD}><AdminRef adminId={r.approvedByAdminId} empty="Member action" /></td>
                    <td className={TD}><Time ms={r.createdAt} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </QueryState>
        </Card>
      )}
    </div>
  )
}

/* ── EA-07 버튼 → EA-43·EA-44·EA-45 ─────────────────────────────── */

type Mode = 'status' | 'grade' | 'expel' | 'penalty'

function MemberActions({ member }: { member: MemberDetail }) {
  const { has } = useSession()
  const [mode, setMode] = useState<Mode | null>(null)

  // ⚠ 탈퇴 회원은 버튼 없음(명세 EA-07).
  if (member.status === 'WITHDRAWN') return null
  const canStatus = has('members.status.edit')
  const canGrade = has('members.grade.edit')
  if (!canStatus && !canGrade) return null
  // 거래가 끝나면 정지로 걸어 둔 회원: 상태는 ACTIVE 지만 단추는 해제(걸어 둔 것을 지운다)
  const waiting = (member.pendingSuspension ?? null) !== null
  const penaltyLocked = member.penaltyLock?.locked === true

  return (
    <>
      {canStatus && penaltyLocked && (
        <Button tone="plain" onClick={() => setMode('penalty')}>Release penalty lock</Button>
      )}
      {canStatus && member.status === 'ACTIVE' && !waiting && (
        <Button tone="plain" onClick={() => setMode('status')}>Suspend</Button>
      )}
      {canStatus && (member.status === 'SUSPENDED' || (member.status === 'ACTIVE' && waiting)) && (
        <Button tone="plain" onClick={() => setMode('status')}>Lift suspension</Button>
      )}
      {canGrade && (member.grade === 'GENERAL' || member.grade === 'SPECIAL') && (
        <Button tone="plain" onClick={() => setMode('grade')}>Change grade</Button>
      )}
      {canGrade && member.grade === 'SPECIAL' && (
        <Button tone="plain" onClick={() => setMode('expel')}>Expel</Button>
      )}
      {mode === 'status' && <StatusDialog member={member} onClose={() => setMode(null)} />}
      {mode === 'grade' && <GradeDialog member={member} onClose={() => setMode(null)} />}
      {mode === 'expel' && <ExpelDialog member={member} onClose={() => setMode(null)} />}
      {mode === 'penalty' && <PenaltyUnlockDialog member={member} onClose={() => setMode(null)} />}
    </>
  )
}

/** 쓰기가 끝나면 이 회원의 상세·이력과 목록을 다시 읽는다. */
function useMemberRefresh(memberId: number) {
  const qc = useQueryClient()
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['member', memberId] }),
      qc.invalidateQueries({ queryKey: ['grade-history', memberId] }),
      qc.invalidateQueries({ queryKey: ['members'] }),
    ])
  }
}

/**
 * EA-43 — 회원 정지·해제(XM-26).
 *
 * 정지 기준(사장님 2026-10-02 「거래 정지 기준」): 매칭 전이면 곧바로 정지(대기 콜은 서버가 내린다), 거래 중이면 그 거래가
 * 끝나면 정지 — 답의 `pendingSuspension` 으로 알고 알림(warn)을 띄운다. 걸어 둔 회원의 해제는 걸어 둔 것을 지운다.
 */
function StatusDialog({ member, onClose }: { member: MemberDetail; onClose: () => void }) {
  const refresh = useMemberRefresh(member.memberId)
  const [reason, setReason] = useState('')
  const w = useWrite()
  const waiting = member.pendingSuspension ?? null
  const suspend = member.status === 'ACTIVE' && waiting === null
  const next = suspend ? 'SUSPENDED' : 'ACTIVE'

  async function submit() {
    const send = async () => {
      const result = await setMemberStatus(member.memberId, next, reason.trim() || undefined)
      const set = result.pendingSuspension ?? null
      if (suspend && set !== null) {
        setFlash({
          tone: 'warn',
          text: `${member.loginId} is in trade ${set.tradeId}. The suspension takes effect when that trade is `
            + 'completed or cancelled.',
        })
      } else if (waiting !== null) {
        setFlash({
          tone: 'ok',
          text: `Lifted the suspension of ${member.loginId} set for the end of trade ${waiting.tradeId}.`,
        })
      } else {
        setFlash({
          tone: 'ok',
          text: suspend
            ? `Suspended ${member.loginId}.`
            : `Lifted the suspension of ${member.loginId}.`,
        })
      }
      await refresh()
    }
    await submitWrite(w, suspend ? 'Suspend member' : 'Lift member suspension', send, onClose, {
      // ⚠ 409(탈퇴·이미 그 상태·이미 걸어 둠)면 화면이 낡았다 — 상세를 다시 읽는다(명세 EA-43).
      onError: (err) => { if (err instanceof ApiError && err.status === 409) void refresh() },
    })
  }

  // ⚠ 정지는 해제할 수 있다 — 「이 작업은 되돌릴 수 없습니다.」를 붙이지 않는다(밤 시험 X-04).
  return (
    <Confirm open title={suspend ? 'Suspend member' : 'Lift suspension'} action={suspend ? 'Suspend' : 'Lift'}
      danger={suspend} irreversible={false} busy={w.busy} error={w.error} onCancel={onClose}
      onConfirm={() => { void submit() }}>
      <div className="flex flex-col gap-3">
        <p>
          {suspend
            ? <>Suspends <strong>{member.loginId}</strong>.</>
            : waiting !== null
              ? <>Lifts the suspension of <strong>{member.loginId}</strong> set for the end of trade{' '}
                  <strong>{waiting.tradeId}</strong>. The member stays normal.</>
              : <>Lifts the suspension of <strong>{member.loginId}</strong>.</>}
        </p>
        <p className="text-xs text-slate-500">
          While suspended, this member can use nothing in the exchange app except viewing the sign-in
          information, signing out and My page. Lifting the suspension lets the member use it again.
        </p>
        {/* ⚠ 마스터 정지는 산하까지 닿는다 — 확인 33(Q-40 「2」): 산하 스페셜 거래도 막음,
            사용료는 계속(R-XB-17). 서버가 막고, 화면은 누르기 전에 알린다. */}
        {suspend && member.grade === 'MASTER' && (
          <Notice tone="warn">
            Suspending a master also blocks trading by the special members under them. The collateral fee keeps
            being deducted during the suspension.
          </Notice>
        )}
        <TextArea label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
    </Confirm>
  )
}

/**
 * 패널티 잠금 해제(XM-125, 2026-10-29 사장님 앱 수정 D 「관리자가 풀 때까지 잠금」 — 해제 원하면 고객센터 직접 전화). 서버가 지갑 서버와
 * 거래소 앱에 알리고 감사 기록을 남긴다. 409(이미 풀림)면 화면이 낡았다 — 상세를 다시 읽는다.
 */
function PenaltyUnlockDialog({ member, onClose }: { member: MemberDetail; onClose: () => void }) {
  const refresh = useMemberRefresh(member.memberId)
  const [reason, setReason] = useState('')
  const w = useWrite()

  async function submit() {
    const send = async () => {
      await releasePenaltyLock(member.memberId, reason.trim() || undefined)
      setFlash({ tone: 'ok', text: `Released the penalty lock of ${member.loginId}.` })
      await refresh()
    }
    await submitWrite(w, 'Release penalty lock', send, onClose, {
      onError: (err) => { if (err instanceof ApiError && err.status === 409) void refresh() },
    })
  }

  return (
    <Confirm open title="Release penalty lock" action="Release" danger={false} irreversible={false} busy={w.busy}
      error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}>
      <div className="flex flex-col gap-3">
        <p>
          Releases the penalty lock of <strong>{member.loginId}</strong>
          {member.penaltyLock?.orderId ? <> (trade <strong>{member.penaltyLock.orderId}</strong>)</> : null}.
        </p>
        <p className="text-xs text-slate-500">
          The member can use the exchange again, and the wallet lock is released too.
        </p>
        <TextArea label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
    </Confirm>
  )
}

/** EA-44 — 회원 등급 변경(XM-27). 길은 둘뿐: 일반 → 스페셜, 스페셜 → 일반. */
function GradeDialog({ member, onClose }: { member: MemberDetail; onClose: () => void }) {
  const refresh = useMemberRefresh(member.memberId)
  const [master, setMaster] = useState('')
  const [reason, setReason] = useState('')
  const w = useWrite()
  // ⚠ 마스터 등급은 여기서 못 바꾼다 — 파트너 승인·철회·박탈로만(`MemberAdminService.changeGrade`).
  const target: Exclude<MemberGrade, 'MASTER'> = member.grade === 'GENERAL' ? 'SPECIAL' : 'GENERAL'
  const toSpecial = target === 'SPECIAL'

  async function submit() {
    await w.run(async () => {
      let masterId: number | undefined
      if (toSpecial) {
        try {
          const found = await findMemberId(master, 'MASTER')
          if (found === null) { w.setError('There is no such master.'); return }
          masterId = found
        } catch (err) {
          w.setError(errorMessage(err)); return
        }
      }
      const send = async () => {
        await changeMemberGrade(member.memberId, target, reason.trim(), masterId)
        setFlash({ tone: 'ok', text: `Changed the grade of ${member.loginId} to ${GRADE_LABEL[target]}.` })
        await refresh()
      }
      await sendWrite(w, 'Change member grade', send, onClose)
    })
  }

  return (
    <Confirm open title={`Change grade of ${member.loginId}`} action="Change" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={reason.trim() === '' || (toSpecial && master.trim() === '')}>
      <div className="flex flex-col gap-3">
        <p>
          {GRADE_LABEL[member.grade]} → <strong>{GRADE_LABEL[target]}</strong>
        </p>
        {toSpecial && (
          <Field label="ID of the master to join (required)" value={master}
            onChange={(e) => setMaster(e.target.value)} />
        )}
        <TextArea label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <p className="text-xs text-slate-500">
          An admin changes the grade directly. The reason is kept in the grade history. The master grade cannot be
          changed here.
        </p>
      </div>
    </Confirm>
  )
}

/** EA-45 — 스페셜 퇴출(XM-28). */
function ExpelDialog({ member, onClose }: { member: MemberDetail; onClose: () => void }) {
  const refresh = useMemberRefresh(member.memberId)
  const [reason, setReason] = useState('')
  const w = useWrite()

  async function submit() {
    const send = async () => {
      await expelMember(member.memberId, reason.trim())
      setFlash({ tone: 'ok', text: `Expelled ${member.loginId}. The member is now a general member.` })
      await refresh()
    }
    await submitWrite(w, 'Expel special member', send, onClose)
  }

  return (
    <Confirm open title="Expel special member" action="Expel" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={reason.trim() === '' || hasKorean(reason)}>
      <div className="flex flex-col gap-3">
        <p>
          Expels <strong>{member.loginId}</strong> from the organization of{' '}
          {member.masterLoginId ?? <Maybe value={null} />}.
        </p>
        <p className="text-xs text-slate-500">
          Once expelled, this member becomes a general member and one seat of that master's capacity opens.
          Trades in progress are finished.
        </p>
        <div className="flex flex-col gap-1">
          <TextArea label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          {/* 사유는 회원에게 영어 메일로 간다(사장님 2026-10-02 「일단 영어로만」) — 한글이면 [Expel] 잠금(서버도 거절). */}
          <EnglishOnlyNote texts={[reason]} hint={REASON_MAIL_HINT}
            blocked="The reason has Korean letters. [Expel] stays off until it is written in English only." />
        </div>
      </div>
    </Confirm>
  )
}
